import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, ChevronRight, AlertTriangle, X, ChevronUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { OneTapReferee } from "@/components/app/OneTapReferee";

const todayKey = () => new Date().toISOString().slice(0, 10);
const LS = "eyfc.actions.popup";

function DueSoon({ n }) {
  return n > 0 ? <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-amber-400/60 text-amber-300 bg-amber-500/10 inline-flex items-center gap-1" data-testid="due-soon-pill"><AlertTriangle className="w-3 h-3" /> Due soon{n > 1 ? ` ×${n}` : ""}</span> : null;
}

function Rows({ items, onGo, onRefresh }) {
  return (
    <ul className="divide-y divide-border" data-testid="actions-rows">
      {items.map(q => (
        <li key={q.key} className="flex items-center gap-3 py-2.5" data-testid={`action-row-${q.key}`}>
          <span className="inline-flex items-center justify-center min-w-[26px] h-[26px] px-1.5 rounded-full bg-[#83B68B]/15 text-[#83B68B] text-xs font-bold">{q.count}</span>
          <span className="flex-1 text-sm">{q.label} <DueSoon n={q.urgent} />
            {q.key === "ref_post_match" && q.games?.length > 0 ? (
              <ul className="mt-1 space-y-1.5 text-[11px]" data-testid="action-detail-ref_post_match">
                {q.games.map(g => <li key={g.booking_id}><span className="opacity-70">{g.label} — {g.missing.join(" · ")}</span>{g.needs_ref && <OneTapReferee game={g} onDone={onRefresh} />}</li>)}
              </ul>
            ) : q.detail?.length > 0 && <ul className="mt-1 space-y-0.5 text-[11px] opacity-70" data-testid={`action-detail-${q.key}`}>{q.detail.map((d, i) => <li key={i}>{d}</li>)}</ul>}
            {q.key === "ref_post_match" && <Link to="/app/expenses" onClick={onGo} className="text-[10px] tracking-widest uppercase underline opacity-70 hover:opacity-100 ml-1" data-testid="action-expenses-link">Expenses</Link>}
          </span>
          <Link to={q.to} onClick={onGo} className="btn-volt text-[10px] tracking-widest uppercase px-3 py-1.5 rounded inline-flex items-center gap-1" data-testid={`action-go-${q.key}`}>Go <ChevronRight className="w-3 h-3" /></Link>
        </li>
      ))}
    </ul>
  );
}

export function ActionsSummary({ data, refresh }) {
  const location = useLocation();
  const [popup, setPopup] = useState(false);
  const [panel, setPanel] = useState(false);
  const [collapsed, setCollapsed] = useState(() => sessionStorage.getItem("eyfc.actions.collapsed") === "1");
  const items = data?.items || [];
  const total = data?.total || 0;
  const urgentItems = useMemo(() => items.filter(i => i.urgent > 0), [items]);
  const urgentOnly = data?.hidden && urgentItems.length > 0;
  const shown = urgentOnly ? urgentItems : items;

  useEffect(() => {
    if (!data || total === 0 || !data.popup_enabled) return;
    if (data.hidden && urgentItems.length === 0) return;
    if (location.search && /[?&](tab|view|swap_target|from_booking)=/.test(location.search)) return; // mid-action deep link
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(LS) || "{}"); } catch { /* ignore */ }
    const count = urgentOnly ? urgentItems.reduce((a, i) => a + i.urgent, 0) : total;
    if (saved.day === todayKey() && (saved.count ?? 0) >= count) return;
    setPopup(true);
  }, [data?.total, data?.hidden, data?.popup_enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissToday = () => {
    localStorage.setItem(LS, JSON.stringify({ day: todayKey(), count: urgentOnly ? urgentItems.reduce((a, i) => a + i.urgent, 0) : total }));
    setPopup(false);
  };
  const hideWeek = async () => {
    dismissToday();
    try { await api.post("/my/action-items/hide", { days: 7 }); refresh?.(); } catch { /* ignore */ }
  };
  const collapse = (v) => { setCollapsed(v); sessionStorage.setItem("eyfc.actions.collapsed", v ? "1" : "0"); };

  if (!data || total === 0) return null;
  const urgentTotal = data.urgent_total || 0;

  return (
    <>
      <Dialog open={popup} onOpenChange={v => { if (!v) dismissToday(); }}>
        <DialogContent className="max-w-lg" data-testid="actions-popup">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#83B68B]" /> You have {urgentOnly ? urgentItems.reduce((a, i) => a + i.urgent, 0) : total} thing{(urgentOnly ? urgentItems.reduce((a, i) => a + i.urgent, 0) : total) === 1 ? "" : "s"} to action
            </DialogTitle>
            <DialogDescription className="text-xs opacity-70">{urgentOnly ? "Only items due within 48 hours are shown while your weekly hide is on." : "Tap Go to jump straight to it."}</DialogDescription>
          </DialogHeader>
          <Rows items={shown} onGo={dismissToday} onRefresh={refresh} />
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={dismissToday} className="flex-1 py-2.5 rounded-md border border-border text-[10px] tracking-widest uppercase font-display font-bold hover:border-white/40" data-testid="actions-remind-tomorrow">Remind me tomorrow</button>
            {!data.hidden && <button type="button" onClick={hideWeek} className="flex-1 py-2.5 rounded-md border border-transparent text-[10px] tracking-widest uppercase opacity-60 hover:opacity-100" data-testid="actions-hide-week">Hide for a week</button>}
          </div>
        </DialogContent>
      </Dialog>

      {collapsed ? (
        <button type="button" onClick={() => collapse(false)} data-testid="actions-bell-badge" title="Items need your action"
          className="fixed bottom-24 md:bottom-6 right-20 md:right-24 z-40 w-11 h-11 rounded-full bg-[#0A0F1C] border border-[#83B68B]/60 text-[#83B68B] shadow-lg inline-flex items-center justify-center">
          <Bell className="w-5 h-5" /><span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold inline-flex items-center justify-center ${urgentTotal ? "bg-amber-500" : "bg-red-500"}`}>{total}</span>
        </button>
      ) : (
        <div className={`border-b ${urgentTotal ? "bg-amber-500/10 border-amber-400/30" : "bg-[#83B68B]/10 border-[#83B68B]/30"}`} data-testid="actions-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-3 text-sm">
            <Bell className={`w-4 h-4 ${urgentTotal ? "text-amber-300" : "text-[#83B68B]"}`} />
            <span className="flex-1"><strong>{total}</strong> item{total === 1 ? "" : "s"} need{total === 1 ? "s" : ""} your action {urgentTotal > 0 && <DueSoon n={urgentTotal} />}</span>
            <button type="button" onClick={() => setPanel(p => !p)} className="text-[10px] tracking-widest uppercase underline" data-testid="actions-banner-view">{panel ? "Hide" : "View"}</button>
            <button type="button" onClick={() => collapse(true)} aria-label="Collapse" className="p-1 opacity-60 hover:opacity-100" data-testid="actions-banner-collapse"><ChevronUp className="w-4 h-4" /></button>
          </div>
          {panel && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3" data-testid="actions-panel">
              <div className="card-tactical rounded-md p-3 relative">
                <button type="button" onClick={() => setPanel(false)} className="absolute top-2 right-2 p-1 opacity-60 hover:opacity-100" aria-label="Close"><X className="w-3.5 h-3.5" /></button>
                <Rows items={items} onGo={() => setPanel(false)} onRefresh={refresh} />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
